import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { Brain, RefreshCw, Loader2, Play, ShieldAlert, Cpu, Activity } from 'lucide-react';

/*
  C4 — ML bot-model admin panel.

  Thin admin UI over the weak-supervision bot-classifier scaffold:
    GET  /api/admin/bot-model/status  — latest model snapshot (or trained:false)
    POST /api/admin/bot-model/train   — retrain on recent clicks (weak labels)
    POST /api/admin/bot-model/score   — score one ad-hoc click-shaped payload

  All three endpoints are admin-gated; a 403 renders a friendly empty state.
  Inference is OFF by default and shadow-only — the model never affects
  live click routing, this page is purely for training and evaluation.
*/

// Prefilled example for the "Score a sample" textarea — a click-shaped
// payload with the fields the feature extractor reads.
const SAMPLE_PAYLOAD = JSON.stringify(
  {
    user_agent: 'Mozilla/5.0 (compatible; Googlebot/2.1; +http://www.google.com/bot.html)',
    referer: '',
    device_type: 'desktop',
    is_unique: false,
  },
  null,
  2
);

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

// Humanized "x ago" for trained_at. Falls back to null when unparseable.
const fmtAgo = (iso, t) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 0) return null;
  if (sec < 60) return t('just now');
  const min = Math.floor(sec / 60);
  if (min < 60) return t('{n} min ago', { n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('{n} h ago', { n: h });
  return t('{n} d ago', { n: Math.floor(h / 24) });
};

const fmtPct = (v) =>
  typeof v === 'number' && Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : '—';

const fmtNum = (v, digits = 4) =>
  typeof v === 'number' && Number.isFinite(v) ? v.toFixed(digits) : '—';

const fmtCount = (v) => (typeof v === 'number' && Number.isFinite(v) ? v.toLocaleString() : '—');

function MetricTile({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-surface-2 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold font-mono">{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

export default function BotModelPanel() {
  const { t } = useT();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState(null);
  const [training, setTraining] = useState(false);
  const [scoreInput, setScoreInput] = useState(SAMPLE_PAYLOAD);
  const [scoring, setScoring] = useState(false);
  const [scoreResult, setScoreResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(API + '/admin/bot-model/status');
      setStatus(res.data || null);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setForbidden(true);
      } else {
        console.error('Error loading bot-model status:', err);
        setError(err?.response?.data?.detail || t('Error loading bot-model status'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const trainModel = async () => {
    try {
      setTraining(true);
      const res = await axios.post(API + '/admin/bot-model/train');
      if (res.data?.status === 'trained') {
        const n = fmtCount(res.data?.n_samples);
        const acc = res.data?.metrics?.train_accuracy ?? res.data?.metrics?.accuracy;
        toast.success(
          typeof acc === 'number'
            ? t('Model trained on {n} clicks — accuracy {acc}', { n, acc: fmtPct(acc) })
            : t('Model trained on {n} clicks', { n })
        );
      } else if (res.data?.status === 'no_data') {
        toast.warning(res.data?.message || t('No click data to train on yet.'));
      } else {
        toast.success(t('Training finished'));
      }
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('Training failed'));
    } finally {
      setTraining(false);
    }
  };

  const scoreSample = async () => {
    let payload;
    try {
      payload = JSON.parse(scoreInput);
    } catch {
      toast.error(t('Invalid JSON — check the payload and try again.'));
      return;
    }
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      toast.error(t('Payload must be a JSON object.'));
      return;
    }
    try {
      setScoring(true);
      const res = await axios.post(API + '/admin/bot-model/score', payload);
      setScoreResult(res.data || null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 404) {
        toast.error(detail || t('No trained model — train one first.'));
      } else {
        toast.error(detail || t('Scoring failed'));
      }
      setScoreResult(null);
    } finally {
      setScoring(false);
    }
  };

  const trained = !!status?.trained;
  const metrics = status?.metrics || {};
  const accuracy = metrics.train_accuracy ?? metrics.accuracy;
  const logLoss = metrics.train_log_loss ?? metrics.log_loss;
  const inferenceOn = !!status?.inference_enabled;
  const trainedAgo = fmtAgo(status?.trained_at, t);

  const mlScore = scoreResult?.ml_bot_score;
  const hasScore = typeof mlScore === 'number' && Number.isFinite(mlScore);
  const isBot =
    typeof scoreResult?.predicted_bot === 'boolean'
      ? scoreResult.predicted_bot
      : hasScore && mlScore >= 0.5;
  const weakLabel = scoreResult?.weak_label;
  const weakLabelText =
    weakLabel === 1 || weakLabel === true
      ? t('Bot')
      : weakLabel === 0 || weakLabel === false
        ? t('Human')
        : '—';

  return (
    <div className="p-8 space-y-8" data-testid="bot-model-panel">
      <XpnsrPageHeader
        kicker={t('Admin')}
        title={t('ML bot model')}
        subtitle={t('Weak-supervision bot classifier: train it on recent clicks, inspect metrics and score ad-hoc payloads. Shadow-only — it never affects live routing.')}
        actions={
          <Button variant="outline" onClick={fetchStatus} disabled={loading}>
            <RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
            {t('Refresh')}
          </Button>
        }
      />

      {forbidden ? (
        <Card>
          <CardContent className="py-10">
            <XpnsrEmpty
              icon={ShieldAlert}
              title={t('Admin access required')}
              subtitle={t('The ML bot-model panel is only available to workspace admins.')}
            />
          </CardContent>
        </Card>
      ) : error ? (
        <Card><CardContent className="py-8 text-center text-rose-600">{error}</CardContent></Card>
      ) : loading && !status ? (
        <div className="skeleton h-96 w-full"></div>
      ) : (
        <>
          <Card data-testid="bot-model-status-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Brain className="w-4 h-4 text-primary" />
                {t('Model status')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={trained ? 'success' : 'secondary'}>
                  {trained ? t('Trained') : t('Not trained')}
                </Badge>
                <Badge variant={inferenceOn ? 'success' : 'outline'}>
                  {t('Inference')}: {inferenceOn ? t('Enabled') : t('Disabled')}
                </Badge>
                {metrics.degenerate ? (
                  <Badge
                    variant="warning"
                    title={t('Trained on a single class or too few samples — metrics are not meaningful.')}
                  >
                    {t('Degenerate')}
                  </Badge>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('Inference is off by default and shadow-only — even when enabled, scores are only recorded on clicks for analysis and never affect routing.')}
              </p>
              <Separator />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MetricTile label={t('Accuracy')} value={fmtPct(accuracy)} />
                <MetricTile label={t('Positive rate')} value={fmtPct(metrics.positive_rate)} />
                <MetricTile label={t('Log loss')} value={fmtNum(logLoss)} />
                <MetricTile label={t('AUC')} value={fmtNum(metrics.auc, 3)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                <Row label={t('Training samples')} value={fmtCount(status?.n_samples ?? metrics.n)} />
                <Row
                  label={t('Trained at')}
                  value={
                    status?.trained_at
                      ? `${fmtTime(status.trained_at)}${trainedAgo ? ` (${trainedAgo})` : ''}`
                      : '—'
                  }
                />
              </div>
              {!trained ? (
                <p className="text-muted-foreground">
                  {t('No bot model trained yet. Train one on recent clicks below.')}
                </p>
              ) : null}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="bot-model-train-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Cpu className="w-4 h-4 text-primary" />
                  {t('Train model')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <p className="text-muted-foreground">
                  {t('Training pulls up to 5,000 recent clicks, weak-labels them with the existing bot heuristics (user agent, uniqueness, behaviour score) and fits a small logistic-regression model. Retraining replaces the stored model.')}
                </p>
                <Button onClick={trainModel} disabled={training} data-testid="train-model-btn">
                  {training ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Cpu className="w-4 h-4 mr-2" />
                  )}
                  {training ? t('Training…') : t('Train model')}
                </Button>
              </CardContent>
            </Card>

            <Card data-testid="bot-model-score-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="w-4 h-4 text-primary" />
                  {t('Score a sample')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="text-muted-foreground">
                  {t('Developer tool: paste a click-shaped JSON payload and run it through the trained model. Nothing is stored and live traffic is unaffected.')}
                </p>
                <Textarea
                  rows={8}
                  value={scoreInput}
                  onChange={(e) => setScoreInput(e.target.value)}
                  className="font-mono text-xs"
                  aria-label={t('Click payload (JSON)')}
                  data-testid="score-payload-input"
                />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={scoreSample} disabled={scoring} data-testid="score-btn">
                    {scoring ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    {scoring ? t('Scoring…') : t('Score')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setScoreInput(SAMPLE_PAYLOAD)}>
                    {t('Reset example')}
                  </Button>
                </div>

                {hasScore ? (
                  <div className="space-y-3" data-testid="score-result">
                    <Separator />
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-2xl font-semibold font-mono">{fmtPct(mlScore)}</span>
                      <Badge variant={isBot ? 'destructive' : 'success'}>
                        {isBot ? t('Likely bot') : t('Likely human')}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {t('Threshold 0.5 — scores above are flagged as bots.')}
                      </span>
                    </div>
                    <Row label={t('Heuristic weak label')} value={weakLabelText} />
                    {scoreResult?.features && typeof scoreResult.features === 'object' ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('Feature')}</TableHead>
                            <TableHead className="text-right">{t('Value')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {Object.entries(scoreResult.features).map(([k, v]) => (
                            <TableRow key={k}>
                              <TableCell className="font-mono text-xs">{k}</TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {typeof v === 'number' ? String(Math.round(v * 10000) / 10000) : String(v)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : null}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
